<template>
  <!-- Read-only list of the knobs for this role (spec 2026-10-03-stellschrauben §9), sysadmins in Profi only.
       Tick rows, "Kopieren" puts a rule with example values and the kind as a comment on the clipboard. -->
  <div class="wmk" data-test="section-knobs">
    <p class="wmk-intro">{{ $t('editor.section.wmk.intro') }}</p>
    <code class="wmk-selector">.wm-bereich[data-rolle="{{ role }}"]</code>
    <div v-for="a in segments" :key="a.key" class="wmk-segment">
      <div class="wmk-segment-title">{{ $t(`editor.section.wmk.segment.${a.key}`) }}</div>
      <label v-for="z in a.rows" :key="z.name" class="wmk-row" :data-name="z.name">
        <input v-model="chosen" type="checkbox" :value="z.name" class="wmk-check">
        <span class="wmk-content">
          <span class="wmk-header">
            <code class="wmk-name">{{ z.name }}</code>
            <span
              class="wmk-kind"
              :class="z.kind === 'fixed' ? 'is-fixed' : 'is-meaning'"
              :title="kindHint(z)"
              data-test="wmk-kind"
            >{{ $t(z.kind === 'fixed' ? 'editor.section.wmk.fixed' : 'editor.section.wmk.meaning') }}</span>
          </span>
          <span class="wmk-text">{{ z.el ? $t('editor.section.wmk.level', { el: elName(z.el), level: z.level }) : z.text }}</span>
          <span v-if="z.all" class="wmk-also">{{ $t('editor.section.wmk.also') }} {{ z.all.filter((n) => n !== z.name).join(', ') }}</span>
        </span>
      </label>
    </div>
    <div class="wmk-footer">
      <button type="button" class="wmk-copy" :disabled="!chosen.length" data-test="wmk-copy" @click="copy">
        {{ copied ? $t('editor.section.wmk.copied') : $t('editor.section.wmk.copy', { n: chosen.length }) }}
      </button>
      <span class="wmk-hint">{{ $t('editor.section.wmk.copyHint') }}</span>
    </div>
  </div>
</template>

<script>
import { knobsSnippet, knobsRows } from './knobs';

const SEGMENTS = ['buttons', 'levels', 'tokens', 'check'];

export default {
  name: 'SectionKnobs',
  props: {
    doc: { type: Object, required: true },
  },
  data() {
    return { chosen: [], copied: false };
  },
  computed: {
    language() {
      return String((this.$i18n && this.$i18n.locale) || 'de').indexOf('en') === 0 ? 'en' : 'de';
    },
    role() {
      return this.doc.section.role;
    },
    rows() {
      return knobsRows(this.doc, this.language);
    },
    segments() {
      return SEGMENTS.map((key) => ({ key, rows: this.rows.filter((z) => z.segment === key) })).filter((a) => a.rows.length);
    },
  },
  watch: {
    role() {
      this.chosen = [];
    },
  },
  methods: {
    kindHint(z) {
      if (z.kind === 'fixed') return this.$t('editor.section.wmk.fixedHint');
      return z.when ? this.$t('editor.section.wmk.meaningWhen', { when: z.when }) : this.$t('editor.section.wmk.meaningHint');
    },
    elName(el) {
      return this.$t(`editor.section.wmk.el.${el}`);
    },
    async copy() {
      const rows = this.rows.filter((z) => this.chosen.includes(z.name));
      const css = knobsSnippet(this.role, rows, {
        meaning: this.$t('editor.section.wmk.meaning'),
        actsWhen: this.$t('editor.section.wmk.actsWhen'),
        fixed: this.$t('editor.section.wmk.fixedComment'),
      });
      try {
        await navigator.clipboard.writeText(css);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1500);
      } catch (e) {
        // No clipboard (http, permissions): the rows stay ticked, nothing else happens.
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// The colours of controls/panel/_panel.scss (importing it would repeat its rules here).
$panel-accent: #4f46e5;
$panel-accent-soft: #f5f6ff;
$panel-border: #e3e6eb;
$panel-line: #f1f3f6;
$panel-muted: #8a919c;
$panel-label: #5b6270;
$panel-text: #1f2430;

.wmk-intro { margin: 0 0 6px; color: $panel-label; }
.wmk-selector { display: block; margin-bottom: 8px; color: $panel-text; font-size: 11px; word-break: break-all; }
.wmk-segment + .wmk-segment { margin-top: 8px; }
.wmk-segment-title { margin-bottom: 2px; color: $panel-muted; font-size: 11px; font-weight: 600; }
.wmk-row { display: flex; gap: 8px; align-items: flex-start; margin: 0; padding: 6px 0; border-top: 1px solid $panel-line; cursor: pointer; }
.wmk-check { flex: none; margin-top: 2px; }
.wmk-content { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.wmk-header { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.wmk-name { color: $panel-text; font-size: 11px; word-break: break-all; }
.wmk-kind { padding: 0 6px; border-radius: 99px; font-size: 10.5px; font-weight: 500; line-height: 18px; white-space: nowrap; }
.wmk-kind.is-meaning { background: $panel-accent-soft; color: $panel-accent; }
.wmk-kind.is-fixed { background: #fef3c7; color: #92400e; }
.wmk-text, .wmk-also { color: $panel-label; font-size: 11px; }
.wmk-also { color: $panel-muted; word-break: break-all; }
// The copy button stays in sight while the long list scrolls.
.wmk-footer { position: sticky; bottom: 0; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 10px; padding: 8px 0; border-top: 1px solid $panel-border; background: #fff; }
.wmk-copy {
  padding: 4px 10px; border: 1px solid $panel-border; border-radius: 6px; background: #fff; color: $panel-text; font-size: 12px; cursor: pointer;
  &:disabled { color: $panel-muted; cursor: default; }
}
.wmk-hint { color: $panel-muted; font-size: 11px; }
</style>
